import React, { useEffect, useMemo, useState } from 'react';
import { Alert, App as AntdApp, Button, Card, Form, Input, Typography } from 'antd';
import { ArrowLeftOutlined, LockOutlined, SafetyCertificateOutlined } from '@ant-design/icons';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import AuthBrandPanel from '../components/auth/AuthBrandPanel';
import { AUTH_PAGE_STYLES } from '../components/auth/authPageStyles';
import { useTheme } from '../contexts/ThemeContext';
import { useSiteBranding } from '../hooks/useSiteBranding';
import { authAPI } from '../services/api';

const { Title, Text } = Typography;

const ResetPassword: React.FC = () => {
  const { message } = AntdApp.useApp();
  const { mode } = useTheme();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = useMemo(() => searchParams.get('token') || '', [searchParams]);
  const email = useMemo(() => searchParams.get('email') || '', [searchParams]);
  const [loading, setLoading] = useState(false);
  const [alertText, setAlertText] = useState<string>('');
  const [logoFailed, setLogoFailed] = useState(false);
  const { slogan, siteName, userLoginLogoUrl } = useSiteBranding();

  useEffect(() => {
    setLogoFailed(false);
  }, [userLoginLogoUrl]);

  const handleSubmit = async (values: { new_password: string; confirm_password: string }) => {
    if (!token) {
      setAlertText('缺少有效的重置链接，请重新申请密码重置邮件。');
      return;
    }

    setLoading(true);
    try {
      const result = await authAPI.resetPassword({ token, new_password: values.new_password });
      message.success(result.message || '密码已重置，请重新登录。');
      navigate('/login?reset=1', { replace: true });
    } catch (err: any) {
      setAlertText(err?.response?.data?.detail || '密码重置失败，请重新获取重置链接。');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`auth-page ${mode === 'dark' ? 'is-dark' : ''}`}>
      <style>{AUTH_PAGE_STYLES}</style>

      <AuthBrandPanel
        description={slogan}
        siteName={siteName}
        logoUrl={userLoginLogoUrl}
        logoFailed={logoFailed}
        onLogoError={() => setLogoFailed(true)}
      />

      <div className="auth-page-right">
        <div className="auth-shell">
          <Card className="auth-card" bordered={false}>
            <div className="auth-topbar">
              <div className="auth-badge">
                <SafetyCertificateOutlined />
                <span>账户恢复</span>
              </div>
              <Link to="/login" className="auth-back-link">
                <ArrowLeftOutlined style={{ marginRight: 6 }} />
                返回登录
              </Link>
            </div>

            <div className="auth-header">
              <Title className="auth-title">设置新密码</Title>
              <Text className="auth-subtitle">
                {email ? `为 ${email} 设置新的登录密码。` : '通过邮件链接设置新的登录密码。'}
              </Text>
            </div>

            {!token ? (
              <Alert
                type="error"
                message="缺少有效的重置链接"
                description="请返回登录页重新申请密码重置邮件。"
                showIcon
                style={{ marginBottom: 20, borderRadius: 14 }}
              />
            ) : null}

            {alertText ? (
              <Alert type="error" message={alertText} showIcon style={{ marginBottom: 20, borderRadius: 14 }} />
            ) : null}

            <Form onFinish={handleSubmit} size="large" className="auth-form" layout="vertical">
              <Form.Item
                name="new_password"
                rules={[
                  { required: true, message: '请输入新密码' },
                  { min: 6, message: '密码至少 6 位' },
                ]}
              >
                <Input.Password
                  className="auth-input"
                  prefix={<LockOutlined />}
                  placeholder="新密码"
                  autoComplete="new-password"
                />
              </Form.Item>

              <Form.Item
                name="confirm_password"
                dependencies={['new_password']}
                rules={[
                  { required: true, message: '请再次输入新密码' },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value || getFieldValue('new_password') === value) {
                        return Promise.resolve();
                      }
                      return Promise.reject(new Error('两次输入的密码不一致'));
                    },
                  }),
                ]}
              >
                <Input.Password
                  className="auth-input"
                  prefix={<LockOutlined />}
                  placeholder="确认新密码"
                  autoComplete="new-password"
                />
              </Form.Item>

              <Form.Item style={{ marginBottom: 0 }}>
                <Button className="auth-submit" type="primary" htmlType="submit" loading={loading} block disabled={!token}>
                  保存新密码
                </Button>
              </Form.Item>
            </Form>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
